/**
 * ЭТАП 2 — серверный prefetch данных для SSR-страниц.
 *
 * Реестр «pathname → query-опции»: страницы из реестра при первом (серверном)
 * запросе получают ПОЛНЫЙ контент в HTML — данные префетчатся в per-request
 * QueryClient, дегидрируются в поток (router.dehydrate) и на клиенте
 * гидрируются в синглтон-QueryClient ДО первого рендера. Дальше навигация
 * работает как SPA: реестр на клиенте не блокирует переходы (fire-and-forget),
 * данные тянут обычные useQuery.
 *
 * ВАЖНО про ключи: параметры префетча обязаны БАЙТ-В-БАЙТ совпадать с тем, что
 * передают компоненты на первом клиентском рендере (сторы ещё не загружены:
 * xStoreId === null), иначе кэш не матчится и SSR-HTML выбрасывается.
 *
 * Личное/пер-юзерное сюда НЕ добавлять (SSR без кук пользователя).
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  mobileMenuTreeQueryOptions,
  barMenuCategoriesQueryOptions,
  indexCoreBannersQueryOptions,
  pofdProductsQueryOptions,
  indexProductsRowQueryOptions,
  indexCoreRecipesQueryOptions,
  indexReviewsQueryOptions,
  topNewestReviewsQueryOptions,
  topMenuQueryOptions,
  seoQueryOptions,
  catalogProductsInfiniteQueryOptions,
  bannerAndTextQueryOptions,
  buildBannerAndTextKeyParams,
  catalogFaqQueryOptions,
  popularMainPageItemsQueryOptions,
  recipesRecommendQueryOptions,
  productInfoTempQueryOptions,
  productRecipesQueryOptions,
  productAlsoboughtQueryOptions,
  productReviewsCountQueryOptions,
  productQuestionsCountQueryOptions,
  productHasAttachmentInCommentsQueryOptions,
  promoRecipesByProductIdQueryOptions,
  promotionsQueryOptions,
  promotionByIdInfiniteQueryOptions,
  buildPromotionByIdKeyParams,
  parsePromotionUrlData,
  singleNewsQueryOptions,
  newsAllQueryOptions,
  newsInfiniteQueryOptions,
  resolveNewsSetLimit,
  articleByIdQueryOptions,
  articleRelatedCatalogItemsQueryOptions,
  articleRelatedArticlesQueryOptions,
  articlesInfiniteQueryOptions,
  buildArticlesListParams,
  recipesPopularQueryOptions,
  authorQueryOptions,
  authorRecipesInfiniteQueryOptions,
  AUTHOR_RECIPES_LIMIT,
  reviewsInfiniteQueryOptions,
  buildReviewsListParams,
  averageReviewsRatingQueryOptions,
  reviewsRatingQueryOptions,
  recipesInfiniteQueryOptions,
  buildRecipesListParams,
  parseRecipeUrlId,
  recipeByIdQueryOptions,
  rutubeVideoMetaQueryOptions,
  rangesQueryOptions,
  rangeByIdQueryOptions,
  productsBySupplierInfiniteQueryOptions,
  testDrivesInfiniteQueryOptions,
  faqsAllInfiniteQueryOptions,
  askChefInterestingRecipesQueryOptions,
  chefBlogArticlesQueryOptions,
  recipesVideoCodesQueryOptions,
  businessGiftSetsQueryOptions,
  businessCertificatesQueryOptions,
  barsRubricProductsQueryOptions,
  indexAdditionRecommendProductsQueryOptions,
  configValuesQueryOptions
} from '@queries/index';
import {
  getRutubeVideoApiUrl,
  parseVideoUrl
} from '@components/VideoPlayer/parseVideoUrl';
import { pickupPointByIdQueryOptions, shopsQueryOptions } from '@queries/shops';
import { getURLProps } from '@utils/setPagesUrl';
import {
  buildInitialCatalogQueryArgs,
  CATALOG_EPP_DEFAULT
} from './routes/Catalog/components/CatalogCategory/catalogInitialParams';
import { BARS_INITIAL_TAB_RUBRICS } from '@components/LandingBlocks/OysterBars/barsRubrics';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyQueryOptions = any;

const decodePathSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * Общие запросы ЛЮБОЙ SSR-страницы: шапка (верхнее меню-коллекции) рендерится
 * в персистентном Layout-Shell на каждой странице.
 */
const globalQueries = (pathname: string): AnyQueryOptions[] => [
  topMenuQueryOptions(),
  // Мета-теги страницы (REST meta.get) — рендерятся CacheDataRoute на КАЖДОЙ
  // странице; без префетча SSR отдаёт страницу вовсе без меты.
  seoQueryOptions(pathname)
];

/**
 * Главная — ВЕСЬ публичный контент (контракт: SSR отдаёт всё, кроме явно
 * исключённых мест): слайдер-баннеры, товар дня, три товарные карусели,
 * рецепты «Приготовьте сегодня», «Отзывы о нас», отзывы о товарах.
 * Исключения: сторисы — НАМЕРЕННО client-only (решение Sergei): query не
 * префетчится, SSR рендерит их встроенный скелетон; запросы «Возможностей»
 * (taskList/testDrivesCount) — персональные (enabled: isLoggedIn), в SSR
 * не ходят по общему правилу реестра.
 */
const mainPageQueries = (): AnyQueryOptions[] => [
  indexCoreBannersQueryOptions({}),
  indexCoreRecipesQueryOptions(),
  indexReviewsQueryOptions(),
  topNewestReviewsQueryOptions(),
  pofdProductsQueryOptions(null),
  indexProductsRowQueryOptions({
    resolver: 'getIndexAddition',
    layer: 'recommendProducts',
    xStoreId: null
  }),
  indexProductsRowQueryOptions({
    resolver: 'getIndexCore',
    layer: 'newbeeProducts',
    xStoreId: null
  }),
  indexProductsRowQueryOptions({
    resolver: 'getIndexCore',
    layer: 'popularProducts',
    xStoreId: null
  })
];

/**
 * /areas — витрина зон бесплатной доставки: одна справочная выборка
 * (getRanges) без параметров, из неё же собираются ссылки на /delivery/:id.
 * Параметров в URL нет, поэтому точное совпадение pathname, а не паттерн.
 */
const areasQueries = (): AnyQueryOptions[] => [rangesQueryOptions()];

/**
 * /delivery — страница доставки. В SSR уходит только справочник зон
 * (getRanges, тот же ключ, что у /areas). Пункты самовывоза и ближайшая
 * доставка НЕ префетчатся СОЗНАТЕЛЬНО: их ключ содержит cartToken, а он на
 * сервере — случайный v1() с module-eval (localStorage там нет), т.е.
 * серверный ключ НИКОГДА не совпадёт с клиентским. Префетч был бы выброшен,
 * а SSR-разметка блоков разошлась бы с первым клиентским рендером
 * (hydration mismatch). Эти блоки остаются клиентскими — как и любые
 * данные, привязанные к корзине/юзеру.
 */
const deliveryQueries = (): AnyQueryOptions[] => [rangesQueryOptions()];

/**
 * Блог шеф-повара /cook — ВЕСЬ рендерящийся контент страницы:
 *  - «Полезные статьи» (общая фабрика с компонентом, параметры входят в ключ);
 *  - «Видеорецепты» (плееры внутри lazy+Suspense, react-youtube SSR-safe:
 *    у него `if (typeof document === 'undefined') return`);
 *  - карусель рецептов в RecipesContent — это ТОТ ЖЕ ключ, что и блок
 *    «Приготовьте сегодня» на главной (useIndexCoreRecipesQuery).
 * Баннеры, блок шефа и поиск по рецептам — статика, данных не просят.
 * Страницы /cook/:id тут нет намеренно — раздел отключён (решение Sergei).
 */
const chefBlogQueries = (): AnyQueryOptions[] => [
  chefBlogArticlesQueryOptions(),
  recipesVideoCodesQueryOptions(),
  indexCoreRecipesQueryOptions()
];

/**
 * «Бизнесу» /opt — статичная лендинговая страница, но в теле две ТОВАРНЫЕ
 * подборки (готовые наборы и сертификаты), они и нужны в SSR. Ключи через
 * общие фабрики, параметры целиком входят в ключ.
 * Форма заявки, загрузка файла и копирование ЭДО-идентификатора —
 * интерактив, данных не просят.
 */
const businessQueries = (): AnyQueryOptions[] => [
  businessGiftSetsQueryOptions(),
  businessCertificatesQueryOptions()
];

/**
 * Контакты /contacts — статичный текст плюс блок ShopsMap со СПИСКОМ
 * магазинов (getShops). Раньше блок читал cartStore.shops и на сервере не
 * рендерился вовсе; теперь читает react-query тем же ключом.
 * Карта Яндекса и баннеры внизу (PromoBanners зависит от пользователя) —
 * client-only.
 */
const contactsQueries = (): AnyQueryOptions[] => [shopsQueryOptions()];

/**
 * Лендинг гастрономов /store: блок «Наши адреса» (OurAddresses -> useShops,
 * тот же ключ getShops) и «Отзывы о нас» (indexReviews с лимитом 20 — ТОТ ЖЕ
 * ключ, что и блок отзывов на главной, у него дефолт тоже 20).
 * Баннеры, «Оплата бонусами», «Отдых с питомцами» и вопросы — статика.
 * Карта внутри «Наших адресов» — client-only.
 */
const storeLandingQueries = (): AnyQueryOptions[] => [
  shopsQueryOptions(),
  indexReviewsQueryOptions(20)
];

/**
 * Лендинг устричных баров /ustrichnye-bary-v-moskve. Данных на нём два вида:
 *  - магазины (getShops) — их просят СРАЗУ ТРИ блока через useShops:
 *    «Наши адреса», «Забронировать стол» и «Атмосфера» (последний берёт из
 *    того же ответа фотографии). Ключ один, запрос тоже один;
 *  - стартовые вкладки «Устрицы» и «Фестиваль ежей» — getProducts по рубрике
 *    бар-меню (`barsRubricProductsQueryOptions`, общая фабрика со слайдером
 *    BarsProductsSlider; на лендинге без menuStoreId). Вторые вкладки
 *    («Хиты меню», «Вкус севера») грузятся по клику.
 * Виджет отзывов (SmartWidgetReviews) — сторонний скрипт, подключается
 * эффектом и остаётся client-only. Остальные секции статичны.
 */
const oysterBarLandingQueries = (): AnyQueryOptions[] => [
  shopsQueryOptions(),
  ...BARS_INITIAL_TAB_RUBRICS.map(type => barsRubricProductsQueryOptions(type))
];

/**
 * Лендинги гастрономов: /shop (общий) и /shop-<identify> (конкретный).
 * Данных два вида:
 *  - магазины (getShops): «Наши адреса» на обеих страницах, а на /shop-<id>
 *    из того же списка ищется и сам магазин (см. loader роута);
 *  - «Хиты по суперценам» — это AuthorMenu с type='main-hits', то есть ДРУГАЯ
 *    его ветка: getIndexAddition, а не список id, как на барных лендингах.
 *    Лимит 6 продублирован из компонента — держать синхронно.
 * Остальные секции (ShopBanner, ShopBarInfo, ShopInfo, ShopPros, баннеры,
 * SEO-текст) статичны; виджет отзывов — сторонний скрипт, client-only.
 */
const shopLandingQueries = (): AnyQueryOptions[] => [
  shopsQueryOptions(),
  indexAdditionRecommendProductsQueryOptions(6)
];

/**
 * Оферта для курьеров /oferta-courier. Страница целиком статична, кроме
 * ОДНОЙ ссылки — она лежит в конфиге сайта (`config.value_list`), а рендер
 * на ней гейтится: без значения компонент отдаёт LoadingPage. Держателем
 * конфига был только mobx-стор (наполняется эффектом), поэтому сервер
 * отдавал пустую страницу.
 */
const courierOfertaQueries = (): AnyQueryOptions[] => [
  configValuesQueryOptions()
];

const SSR_PREFETCH: Record<string, () => AnyQueryOptions[]> = {
  '/': mainPageQueries,
  '/areas': areasQueries,
  '/areas/': areasQueries,
  '/delivery': deliveryQueries,
  '/delivery/': deliveryQueries,
  '/cook': chefBlogQueries,
  '/cook/': chefBlogQueries,
  '/opt': businessQueries,
  '/opt/': businessQueries,
  '/contacts': contactsQueries,
  '/contacts/': contactsQueries,
  '/store': storeLandingQueries,
  '/store/': storeLandingQueries,
  '/ustrichnye-bary-v-moskve': oysterBarLandingQueries,
  '/ustrichnye-bary-v-moskve/': oysterBarLandingQueries,
  '/shop': shopLandingQueries,
  '/shop/': shopLandingQueries,
  '/oferta-courier': courierOfertaQueries,
  '/oferta-courier/': courierOfertaQueries
};

/**
 * Паттерн-страницы (не точное совпадение pathname). Каталог /catalog/:id
 * (+ /r/-фильтры, + ?groups=): infinite-запрос товаров — начальные параметры
 * зеркалят первый клиентский рендер (см. catalogInitialParams).
 */
const patternQueries = (
  pathname: string,
  search: string
): AnyQueryOptions[] => {
  const out: AnyQueryOptions[] = [];

  // Весь раздел каталога ('/catalog' и '/catalog/$id'): меню категорий —
  // SSR-фолбэк useCatalogMenu (CatalogMain: плитки, CatalogCategory: сайдбар).
  // Pristine-параметры зеркалят enabled:false-чтения хука — ключи совпадают.
  if (pathname === '/catalog' || pathname.startsWith('/catalog/')) {
    out.push(
      mobileMenuTreeQueryOptions({
        is18plus: false,
        withChildren: true,
        device: 'desktop'
      }),
      barMenuCategoriesQueryOptions({ withChildren: true })
    );
  }

  // Корень каталога: нижняя карусель «Популярные товары» (CatalogMain) —
  // на категориях она приходит из bannerAndText (ниже), на корне — отдельным
  // запросом. Ключ зеркалит первый клиентский рендер (xStoreId null).
  if (pathname === '/catalog') {
    out.push(popularMainPageItemsQueryOptions(null));
  }

  const catalog = buildInitialCatalogQueryArgs(pathname, search);
  if (catalog) {
    out.push(
      catalogProductsInfiniteQueryOptions(catalog.params, catalog.initialPage),
      // Баннер/SEO-текст/ХЛЕБНЫЕ КРОШКИ рубрики — pristine-ключ зеркалит
      // первый клиентский рендер (settings по умолчанию, xStoreId null)
      bannerAndTextQueryOptions(
        buildBannerAndTextKeyParams(pathname, catalog.params.slug, {
          catalogSorting: 'popular',
          eppCatalog: 20,
          xStoreId: null
        })
      )
    );

    // Рецепт-рекомендация — блок «рецепт + SEO-текст» под «Популярными
    // товарами» (/catalog/:id; на services и bar не рендерится)
    if (catalog.params.slug !== 'services' && catalog.params.slug !== 'bar') {
      out.push(recipesRecommendQueryOptions({ xStoreId: null }));
      if (/^\/catalog\/[^/?#]+\/?$/.test(pathname)) {
        out.push(catalogFaqQueryOptions(catalog.params.slug));
      }
    }
  }

  // Страница товара /product/:id — весь публичный контент карточки:
  // сам товар, крошки/описание (внутри ответа), счётчики отзывов/вопросов,
  // «С этим товаром покупают» + «Рекомендуем», рецепты товара, «Готовьте как
  // Шеф» (PromoRecipes). Аналоги НЕ префетчатся (зависят от ответа товара —
  // клиентская ветка для недоступных). Личное (isFavorite, цены магазина
  // юзера) доезжает на клиенте: xStoreId сменится null -> id => перезапрос.
  const productMatch = /^\/product\/(\d+)/.exec(pathname);
  if (productMatch) {
    const productId = Number(productMatch[1]);
    out.push(
      productInfoTempQueryOptions({
        id: productId,
        isMenu: false,
        xStoreId: null
      }),
      productRecipesQueryOptions(productId),
      productAlsoboughtQueryOptions(productId, null),
      productReviewsCountQueryOptions(productId),
      productQuestionsCountQueryOptions(productId),
      productHasAttachmentInCommentsQueryOptions(productId),
      promoRecipesByProductIdQueryOptions(productId)
    );
  }

  // Список акций: /promotions (и легаси /promotions/r/$ — тот же список).
  // Дефолтные параметры зеркалят usePromotionsQuery() без аргументов.
  if (
    pathname === '/promotions' ||
    pathname === '/promotions/' ||
    pathname.startsWith('/promotions/r/') ||
    pathname === '/promotions/r'
  ) {
    out.push(promotionsQueryOptions());
  }

  // Страница акции /promotions/:id («353» или «festival-kraba-353»):
  // infinite-запрос с pristine-ключом (buildPromotionByIdKeyParams — ЕДИНЫЙ
  // источник условных спредов id/identify с компонентом). ?type= и ?page=
  // разбираются как в компоненте (queryRubric/queryPage). Сторисы акции —
  // client-only (как на главной, решение Sergei).
  const promoMatch = /^\/promotions\/([^/?#]+)/.exec(pathname);
  if (promoMatch && promoMatch[1] !== 'r') {
    const promoUrlData = parsePromotionUrlData(promoMatch[1]);
    if (promoUrlData.id || promoUrlData.identify) {
      const searchParams = new URLSearchParams(search);
      out.push(
        promotionByIdInfiniteQueryOptions(
          buildPromotionByIdKeyParams(
            promoUrlData,
            searchParams.get('type'),
            null
          ),
          Number(searchParams.get('page') || 0) || 1
        )
      );
    }
  }

  // Новости: страница новости /news/$id (числовой id) — сама новость +
  // слайдер «Это интересно» (ключ фабрики, select клиентский); комментарии —
  // лениво на клиенте (как отзывы товара). Список /news (+ /news/r/:
  // /r/last/N — размер страницы, /r/page/N — стартовая) — infinite-запрос,
  // ключ {setLimit} через общий resolveNewsSetLimit.
  const newsSingleMatch = /^\/news\/(\d+)/.exec(pathname);
  if (newsSingleMatch) {
    out.push(
      singleNewsQueryOptions(Number(newsSingleMatch[1])),
      newsAllQueryOptions({ setLimit: 10, page: 1 })
    );
  } else if (
    pathname === '/news' ||
    pathname === '/news/' ||
    pathname === '/news/r' ||
    pathname.startsWith('/news/r/')
  ) {
    const newsUrlProps = getURLProps({ pathname });
    out.push(
      newsInfiniteQueryOptions(
        { setLimit: resolveNewsSetLimit(newsUrlProps.last) },
        parseInt(`${newsUrlProps.page}`, 10) || 1
      )
    );
  }

  // Лента: статья /lenta/$id (числовой id) — сама статья (id СТРОКОЙ, как в
  // ключе компонента; select с оглавлением — изоморфный addIdToH2),
  // сопутствующие товары (xStoreId null), похожие статьи, популярные рецепты
  // (RecipesCarousel внизу). Комментарии — лениво на клиенте. Список /lenta
  // (+ /lenta/r/: /r/page/N, /r/search/…) — infinite-запрос, параметры через
  // общий buildArticlesListParams.
  const lentaSingleMatch = /^\/lenta\/(\d+)/.exec(pathname);
  if (lentaSingleMatch) {
    const articleId = lentaSingleMatch[1];
    out.push(
      articleByIdQueryOptions(articleId),
      articleRelatedCatalogItemsQueryOptions(Number(articleId), null),
      articleRelatedArticlesQueryOptions(Number(articleId)),
      recipesPopularQueryOptions({})
    );
  } else if (
    pathname === '/lenta' ||
    pathname === '/lenta/' ||
    pathname === '/lenta/r' ||
    pathname.startsWith('/lenta/r/')
  ) {
    const lentaUrlProps = getURLProps({ pathname });
    out.push(
      articlesInfiniteQueryOptions(
        buildArticlesListParams(lentaUrlProps.search || undefined),
        parseInt(`${lentaUrlProps.page}`, 10) || 1
      )
    );
  }

  // Отзывы о магазине /reviews (+ /reviews/r/: /r/sort/, /r/source/,
  // /r/page/N): список с фильтрами через общий buildReviewsListParams +
  // правый блок «Наш рейтинг» (средняя оценка и разбивка по звёздам —
  // раньше императивный Promise.all, теперь тоже SSR).
  if (
    pathname === '/reviews' ||
    pathname === '/reviews/' ||
    pathname === '/reviews/r' ||
    pathname.startsWith('/reviews/r/')
  ) {
    const reviews = buildReviewsListParams(pathname);
    out.push(
      reviewsInfiniteQueryOptions(reviews.params, reviews.page),
      averageReviewsRatingQueryOptions(),
      reviewsRatingQueryOptions()
    );
  }

  // Товары поставщика /supplier/<id> (только числовой id): pristine-ключ
  // зеркалит первый клиентский рендер — setLimit из дефолтных настроек
  // пользователя (CATALOG_EPP_DEFAULT = EPP_CATALOG_DEFAULT в UserStore),
  // xStoreId null. Сам запрос в компоненте выключен, пока не готов магазин,
  // но disabled-query читает этот кэш — так же, как в каталоге.
  const supplierMatch = /^\/supplier\/(\d+)$/.exec(pathname);
  if (supplierMatch) {
    out.push(
      productsBySupplierInfiniteQueryOptions({
        supplierId: Number(supplierMatch[1]),
        setLimit: CATALOG_EPP_DEFAULT,
        xStoreId: null
      })
    );
  }

  // Зона доставки /delivery/<identify>: сама зона (getRangeById) + баннеры
  // «Акции недели» + ряд «Популярное» (тот же ключ, что на главной:
  // xStoreId pristine null). Пункты самовывоза сюда не входят — см.
  // комментарий к deliveryQueries.
  const deliveryAreaMatch = /^\/delivery\/([^/?#]+)/.exec(pathname);
  if (deliveryAreaMatch) {
    out.push(
      rangeByIdQueryOptions(decodePathSegment(deliveryAreaMatch[1])),
      indexCoreBannersQueryOptions({
        bannersLimit: 10,
        bannersImageSize: 'w600'
      }),
      indexProductsRowQueryOptions({
        resolver: 'getIndexCore',
        layer: 'popularProducts',
        xStoreId: null
      })
    );
  }

  // Рецепт /recipes/<slug>-<id> (или /recipes/<id>): сам рецепт + карусель
  // популярных (excludeIds: [id] — ключ зеркалит компонент).
  const recipeSingleMatch = /^\/recipes\/([^/?#]+)/.exec(pathname);
  if (recipeSingleMatch && recipeSingleMatch[1] !== 'r') {
    const recipeId = parseRecipeUrlId(decodePathSegment(recipeSingleMatch[1]));
    if (Number.isFinite(recipeId)) {
      out.push(
        recipeByIdQueryOptions(recipeId),
        recipesPopularQueryOptions({ excludeIds: [recipeId] })
      );
    }
  } else if (
    pathname === '/recipes' ||
    pathname === '/recipes/' ||
    pathname === '/recipes/r' ||
    pathname.startsWith('/recipes/r/')
  ) {
    // Список рецептов: фильтры (/r/bludo/, /r/type/, /r/search/,
    // /r/productPromo/1 и т.д.) и страница — через общий
    // buildRecipesListParams; sideData фильтров приходит тем же ответом.
    // Плюс карусель «Популярные рецепты» (тот же ключ, что у компонента).
    const recipes = buildRecipesListParams(pathname);
    out.push(
      recipesInfiniteQueryOptions(recipes.params, recipes.page),
      recipesPopularQueryOptions({})
    );
  }

  // Тест-драйвы /test-drive (+ /test-drive/r/page/N). Ключ запроса НЕ
  // содержит страницу (['testDrives','infinite']) — стартовая страница
  // задаётся только initialPageParam, поэтому на сервере её обязательно
  // брать из адреса, иначе прямой заход на /r/page/2 отдал бы первую
  // страницу. Вкладка «Мои тест-драйвы» и модалка участия — персональные,
  // остаются client-only.
  if (
    pathname === '/test-drive' ||
    pathname === '/test-drive/' ||
    pathname === '/test-drive/r' ||
    pathname.startsWith('/test-drive/r/')
  ) {
    const testDriveUrlProps = getURLProps({ pathname });
    out.push(
      testDrivesInfiniteQueryOptions(
        parseInt(`${testDriveUrlProps.page}`, 10) || 1
      )
    );
  }

  // Вопросы шефу /faq (+ /faq/r/page/N) — список вопросов и правый блок
  // «Это интересно» (2 популярных рецепта, общая фабрика с компонентом).
  // Ключ списка содержит строку поиска и флаг «мои вопросы»: и то и другое
  // живёт в стейте компонента, на первом рендере пустое/false — значит и
  // префетч обязан идти ровно с этими значениями, иначе после гидрации
  // компонент промахнётся мимо кэша. Страница — только из адреса.
  // Поиск и «мои вопросы» персональные/интерактивные, в SSR не попадают.
  if (
    pathname === '/faq' ||
    pathname === '/faq/' ||
    pathname === '/faq/r' ||
    pathname.startsWith('/faq/r/')
  ) {
    const faqUrlProps = getURLProps({ pathname });
    out.push(
      faqsAllInfiniteQueryOptions({
        search: '',
        mePeopleIdFilter: false,
        initialPage: parseInt(`${faqUrlProps.page}`, 10) || 1
      }),
      askChefInterestingRecipesQueryOptions()
    );
  }

  // Лендинг конкретного гастронома /shop-<identify>: тот же набор, что
  // у общего /shop (см. shopLandingQueries) — сам магазин ищется по identify
  // в списке getShops, отдельного метода у бэка нет.
  if (/^\/shop-[^/?#]+$/.test(pathname)) {
    out.push(...shopLandingQueries());
  }

  // Точка самовывоза /stores/<id>. cartToken в запрос уходит ПУСТОЙ и в ключ
  // не входит (см. pickupPointByIdQueryOptions): серверный токен — случайный
  // v1() процесса, под ним клиент кэш не нашёл бы. Ответ без корзины —
  // карточка магазина без интервалов доставки; интервалы клиент дотянет сам
  // своим токеном сразу после гидрации.
  const storeMatch = /^\/stores\/(\d+)\/?$/.exec(pathname);
  if (storeMatch) {
    out.push(pickupPointByIdQueryOptions(Number(storeMatch[1]), ''));
  }

  return out;
};

/**
 * ЗАВИСИМЫЕ префетчи: запрос, параметры которого известны только из ответа
 * предыдущего. Обычный реестр — плоский список options, здесь же нужна
 * цепочка, поэтому это функции над QueryClient (выполняются параллельно с
 * остальными; последовательность внутри — их забота).
 */
type DependentPrefetch = (queryClient: QueryClient) => Promise<unknown>;

const dependentPrefetches = (pathname: string): DependentPrefetch[] => {
  const out: DependentPrefetch[] = [];

  // Страница автора /author/:name: рецепты запрашиваются по identify ИЗ
  // ОТВЕТА автора (компонент берёт authorInfo.identify, не сегмент URL) —
  // поэтому сначала автор, потом его публикации тем же ключом, что и
  // первый клиентский рендер (setLimit: AUTHOR_RECIPES_LIMIT).
  const authorMatch = /^\/author\/([^/?#]+)/.exec(pathname);
  if (authorMatch) {
    const identify = decodePathSegment(authorMatch[1]);
    out.push(async queryClient => {
      const authorRes = await queryClient.ensureQueryData(
        authorQueryOptions({ identify })
      );
      const authorIdentify = (
        authorRes as { getAuthor?: { identify?: string | null } } | undefined
      )?.getAuthor?.identify;

      if (!authorIdentify) {
        return;
      }

      return await queryClient.prefetchInfiniteQuery(
        authorRecipesInfiniteQueryOptions({
          author: authorIdentify,
          setLimit: AUTHOR_RECIPES_LIMIT
        })
      );
    });
  }

  const recipeMatch = /^\/recipes\/([^/?#]+)/.exec(pathname);
  if (recipeMatch && recipeMatch[1] !== 'r') {
    const recipeId = parseRecipeUrlId(decodePathSegment(recipeMatch[1]));
    if (Number.isFinite(recipeId)) {
      out.push(async queryClient => {
        const recipeRes = await queryClient.ensureQueryData(
          recipeByIdQueryOptions(recipeId)
        );
        const videoCode = (
          recipeRes as
            | { getRecipeById?: { video_code?: string | null } | null }
            | undefined
        )?.getRecipeById?.video_code;
        const parsed = videoCode ? parseVideoUrl(videoCode) : null;
        const apiUrl = parsed ? getRutubeVideoApiUrl(parsed) : null;
        if (!apiUrl) {
          return;
        }
        return await queryClient.prefetchQuery(
          rutubeVideoMetaQueryOptions(apiUrl)
        );
      });
    }
  }

  // Страница бара /ustrichnyj-bar-<identify>: отдельного метода у бара нет,
  // он ищется по identify в списке getShops (нужен трём блокам страницы),
  // а стартовые вкладки «Устрицы» и «Фестиваль ежей» запрашиваются с
  // menuStoreId = id бара из этого ответа (тем же ключом, что BarsPage на
  // первом рендере; у «скоро»-бара — без menuStoreId, как на лендинге).
  // Неизвестный слаг уходит в Redirect.
  const barMatch = /^\/ustrichnyj-bar-([^/?#]+)$/.exec(pathname);
  if (barMatch) {
    const identify = decodePathSegment(barMatch[1]);
    out.push(async queryClient => {
      const shopsRes = await queryClient.ensureQueryData(shopsQueryOptions());
      const shop = shopsRes.getShops?.find(item => item.identify === identify);

      if (!shop) {
        return;
      }

      const menuStoreId =
        !shop.opens_soon && shop.id != null ? shop.id : undefined;

      return await Promise.all(
        BARS_INITIAL_TAB_RUBRICS.map(type =>
          queryClient.prefetchQuery(
            barsRubricProductsQueryOptions(type, menuStoreId)
          )
        )
      );
    });
  }

  return out;
};

export const prefetchForPathname = async (
  queryClient: QueryClient,
  pathname: string,
  search = ''
): Promise<void> => {
  const factory = SSR_PREFETCH[pathname];
  const queries = [
    ...globalQueries(pathname),
    ...(factory ? factory() : []),
    ...patternQueries(pathname, search)
  ];
  const dependents = dependentPrefetches(pathname);

  await Promise.all([
    ...dependents.map(run =>
      run(queryClient).catch((e: unknown) => {
        // Как и с обычными запросами: падение не валит страницу
        console.error('[ssrPrefetch:dependent]', pathname, e);
      })
    ),
    ...queries.map(options =>
      // infiniteQueryOptions отличаются наличием initialPageParam — кэш
      // infinite-запроса имеет другую форму ({pages, pageParams}), префетчить
      // его обычным prefetchQuery нельзя.
      (options.initialPageParam !== undefined
        ? queryClient.prefetchInfiniteQuery(options)
        : queryClient.prefetchQuery(options)
      ).catch((e: unknown) => {
        // Падение одного запроса не валит SSR страницы — блок отрендерится
        // пустым и дозагрузится на клиенте, как раньше.
        console.error('[ssrPrefetch]', pathname, e);
      })
    )
  ]);
};
